import { useFormik } from 'formik';
import * as Yup from 'yup';

Yup.addMethod(Yup.string, 'requiredIfPeersPresent', function (peerFields, message) {
  return this.test({
    name: 'requiredIfPeersPresent',
    exclusive: false,
    message: message || 'This field is required conditionally',
    test(value) {
      const { parent } = this;
      // Check if any of the specified peer fields in the parent object have a truthy value
      const hasAnyPeerField = peerFields.some((field) => Boolean(parent[field]));

      // If a peer field is present, the current field must have a value (value is truthy)
      if (hasAnyPeerField && !value) {
        return false;
      }
      return true;
    }
  });
});

const allFields = ['npi', 'tax_id', 'tax_id_type', 'taxonomy'];
const addressFields = ['address', 'address_2', 'city', 'state', 'zip'];

const CreateUpdateStaffMemberValidationSchema = () =>
  Yup.object().shape({
    f_name: Yup.string().required('First name is required'),
    l_name: Yup.string().required('Last name is required'),
    role: Yup.number().required('The role is required'),
    email: Yup.string().email('Please type a valid format').required('Email is required'),
    phone: Yup.string().required('Phone number is required'),
    gender: Yup.string().required('Gender is required'),
    dob: Yup.date().required('Date of birth is required'),
    address_is_same_as_practice: Yup.boolean().default(false),
    taxonomy: Yup.string()
      .length(10, 'Must be exactly 10 characters')
      .matches(/^[a-zA-Z0-9]+$/, 'Must be alphanumeric'),
    npi: Yup.string()
      .length(10, 'NPI must be exactly 10 digits')
      .matches(/^\d{10}$/, 'NPI must be only digits (0-9)')
      .requiredIfPeersPresent(
        allFields.filter((f) => f !== 'npi'),
        'NPI is required'
      ),
    tax_id: Yup.string()
      .length(9, 'Tax ID must be exactly 9 digits')
      .matches(/^\d{9}$/, 'Tax ID must be only digits (0-9)')
      .requiredIfPeersPresent(
        allFields.filter((f) => f !== 'tax_id'),
        'Tax ID is required'
      ),
    tax_id_type: Yup.string().requiredIfPeersPresent(
      allFields.filter((f) => f !== 'tax_id_type'),
      'Tax ID Type is required'
    ),
    address: Yup.string().when('address_is_same_as_practice', {
      is: false,
      then: (schema) =>
        schema.requiredIfPeersPresent(
          addressFields.filter((f) => f !== 'address'),
          'Address is required'
        )
    }),
    city: Yup.string().when('address_is_same_as_practice', {
      is: false,
      then: (schema) =>
        schema.requiredIfPeersPresent(
          addressFields.filter((f) => f !== 'city'),
          'City is required'
        )
    }),
    state: Yup.string().when('address_is_same_as_practice', {
      is: false,
      then: (schema) =>
        schema.requiredIfPeersPresent(
          addressFields.filter((f) => f !== 'state'),
          'State is required'
        )
    }),
    zip: Yup.string().when('address_is_same_as_practice', {
      is: false,
      then: (schema) =>
        schema
          .matches(/^\d{5}(?:-\d{4})?$/, 'Invalid ZIP Code format (Use 5 digits or 5+4 format)')
          .requiredIfPeersPresent(
            addressFields.filter((f) => f !== 'zip'),
            'ZIP Code is required'
          )
    }),
    local_state_ids: Yup.array().of(Yup.number()).default([]),
    virtual_state_ids: Yup.array().of(Yup.number()).default([])
  });

export default function useNewStaffMemberFormik(staffMember = {}, onSubmit) {
  const formik = useFormik({
    initialValues: {
      f_name: staffMember?.f_name || '',
      m_name: staffMember?.m_name || '',
      l_name: staffMember?.l_name || '',
      gender: staffMember?.gender || null,
      dob: staffMember?.dob || '',
      email: staffMember?.email || '',
      phone: staffMember?.phone || '',
      color: staffMember?.color || '#717680',
      role: staffMember?.role || null,
      is_active: staffMember?.is_active ?? true,
      profession_group_id: staffMember?.profession_group_id || null,
      payment_rate: staffMember?.payment_rate || '0',
      npi: staffMember?.npi || '',
      tax_id: staffMember?.tax_id || '',
      tax_id_type: staffMember?.tax_id_type || '',
      taxonomy: staffMember?.taxonomy || '',
      address: staffMember?.address || '',
      address_2: staffMember?.address_2 || '',
      city: staffMember?.city || '',
      state: staffMember?.state || '',
      zip: staffMember?.zip || '',
      address_is_same_as_practice: false,
      trainings: staffMember?.trainings || [],
      availableTimes: staffMember?.availableTimes || null,
      local_state_ids: Array.isArray(staffMember?.local_state_ids)
        ? staffMember.local_state_ids
        : [],
      virtual_state_ids: Array.isArray(staffMember?.virtual_state_ids)
        ? staffMember.virtual_state_ids
        : []
    },
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: CreateUpdateStaffMemberValidationSchema,
    onSubmit
  });

  return formik;
}
